import {
  ArrowBack as ArrowBackIcon,
  FileDownload as DownloadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { DateUtils } from '../../utils';
import { useGetActiveManufacturersQuery } from '../manufacturer/manufacturers-api-slice';
import { selectCurrentUser } from '../user/user-slice';
import { RiskToggleBar } from './components/risk-toggle-bar';
import {
  deriveProductLabel,
  exportPriceSheetCsv,
  exportPriceSheetPdf,
  fmtSize,
  productCostPerFoot,
  productCostPerUnit,
} from './export-utils';
import {
  categoryAverageChange,
  hasPreviousMsrp,
  shouldShowPercentChange,
} from './price-sheet-utils';
import { useGetProductPriceSheetQuery } from './price-management-api-slice';
import { ProductPriceItem } from './types';

const fmtCurrency = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

interface FooterRowProps {
  filtered: ProductPriceItem[];
  isRiskII: boolean;
  riskFilteredTotal: number;
}

const FooterRow: React.FC<FooterRowProps> = ({ filtered, isRiskII, riskFilteredTotal }) => {
  const allChanges = filtered
    .map(i => i.priceHistory?.msrpChangePercentage)
    .filter((v): v is number => v !== undefined);
  const avg =
    allChanges.length > 0 ? allChanges.reduce((s, v) => s + v, 0) / allChanges.length : null;
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        bgcolor: 'grey.50',
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Showing {filtered.length} of {riskFilteredTotal} products (
        {isRiskII ? 'Risk II - Permanent Dwelling' : 'Risk I'})
      </Typography>
      {avg !== null && (
        <Typography variant="caption" color="text.secondary">
          Overall Average Change:{' '}
          <strong style={{ color: avg >= 0 ? '#d32f2f' : '#388e3c' }}>
            {avg >= 0 ? '+' : ''}
            {avg.toFixed(2)}%
          </strong>
        </Typography>
      )}
    </Box>
  );
};

interface PercentChangeProps {
  value?: number;
}

const PercentChange: React.FC<PercentChangeProps> = ({ value }) => {
  if (value === undefined || value === null) {
    return (
      <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
        N/A
      </Typography>
    );
  }
  const isPositive = value > 0;
  const isNegative = value < 0;
  return (
    <Typography
      variant="body2"
      sx={{
        fontSize: '0.8125rem',
        fontFamily: 'monospace',
        fontWeight: 500,
        color: isPositive ? 'error.main' : isNegative ? 'success.dark' : 'text.secondary',
      }}
    >
      {isPositive ? '+' : ''}
      {value.toFixed(2)}%
    </Typography>
  );
};

/**
 * Product Price Sheet Page
 * Customer-facing pricing view grouped by category/tier with Risk I/II toggle.
 * Shows Retail Price/Unit (MSRP), dealer Cost/Unit (margin-adjusted), and % change vs old MSRP.
 */
const ProductPriceSheetPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useAppSelector(selectCurrentUser);

  const manufacturerIdParam = searchParams.get('manufacturerId');
  const manufacturerId = manufacturerIdParam ? parseInt(manufacturerIdParam, 10) : undefined;
  const effectiveManufacturerId = manufacturerId ?? currentUser?.manufacturerId;

  const { data: manufacturers = [] } = useGetActiveManufacturersQuery(undefined, {
    skip: !effectiveManufacturerId,
  });

  const { data, isLoading, error } = useGetProductPriceSheetQuery(
    { manufacturerId: effectiveManufacturerId! },
    { skip: !effectiveManufacturerId }
  );

  const [selectedRiskLevel, setSelectedRiskLevel] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [search, setSearch] = useState('');

  // Distinct risk levels from data
  const riskLevels = useMemo(() => {
    if (!data?.products) return [];
    return Array.from(new Set(data.products.map(p => p.riskLevel))).sort();
  }, [data]);

  const effectiveRiskLevel = selectedRiskLevel ?? riskLevels[0] ?? null;

  // Products filtered by risk level
  const riskFiltered = useMemo(() => {
    if (!data?.products) return [];
    if (!effectiveRiskLevel) return data.products;
    return data.products.filter(p => p.riskLevel === effectiveRiskLevel);
  }, [data, effectiveRiskLevel]);

  // Distinct category names for filter tabs
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    riskFiltered.forEach(p => {
      if (!seen.has(p.category)) {
        seen.add(p.category);
        result.push(p.category);
      }
    });
    return result;
  }, [riskFiltered]);

  // Products filtered by category tab and search
  const filtered = useMemo(() => {
    let items = riskFiltered;
    if (activeCategory !== 'all') items = items.filter(p => p.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        p =>
          p.category.toLowerCase().includes(q) ||
          deriveProductLabel(p).toLowerCase().includes(q) ||
          (p.code && p.code.toLowerCase().includes(q)) ||
          String(p.averageSize).includes(q)
      );
    }
    return items;
  }, [riskFiltered, activeCategory, search]);

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map<string, ProductPriceItem[]>();
    filtered.forEach(p => {
      if (!map.has(p.category)) map.set(p.category, []);
      map.get(p.category)!.push(p);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const manufacturerName =
    manufacturers.find(m => m.id === effectiveManufacturerId)?.licenseeName ??
    currentUser?.manufacturer ??
    data?.manufacturerName ??
    '';

  const isRiskII = riskLevels.length > 1 && effectiveRiskLevel === riskLevels[1];

  if (currentUser?.role === UserRole.DEALER) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Product Price Sheet
        </Typography>
        <Alert severity="error" sx={{ mt: 1 }}>
          <strong>Access Restricted</strong> — The Product Price Sheet is not available for Dealer
          accounts. Please contact your manufacturer representative for pricing information.
        </Alert>
      </Box>
    );
  }

  if (!effectiveManufacturerId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Product Price Sheet
        </Typography>
        <Alert severity="warning">
          No manufacturer selected. Please navigate here from the Material Pricing Calculator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Box sx={{ ml: 1 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Product Price Sheet
            </Typography>
            {manufacturerName && (
              <Typography variant="body2" color="text.secondary">
                {manufacturerName}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Download as CSV">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={!data || filtered.length === 0}
                onClick={() => exportPriceSheetCsv(filtered, manufacturerName, data!.generatedAt)}
              >
                CSV
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Download as PDF">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={!data || filtered.length === 0}
                onClick={() =>
                  exportPriceSheetPdf(
                    filtered,
                    manufacturerName,
                    data!.generatedAt,
                    isRiskII ? 'Risk II - Permanent Dwelling' : 'Risk I'
                  )
                }
              >
                PDF
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Info bar */}
      {data && (
        <Paper
          variant="outlined"
          sx={{
            px: 2,
            py: 1,
            mb: 2,
            bgcolor: 'primary.50',
            borderColor: 'primary.200',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" color="primary.dark">
            Based on Published BOM
            {data.bomVersion != null ? ` Version ${data.bomVersion}` : ''}
            {data.effectiveDate
              ? ` (Effective: ${DateUtils.parseDate(data.effectiveDate).toFormat('MMM d, yyyy')})`
              : ''}
          </Typography>
          <Typography variant="body2" color="primary.dark">
            Generated {DateUtils.parseDate(data.generatedAt).toFormat('MMM d, yyyy')}
          </Typography>
        </Paper>
      )}

      {/* Risk level toggle — always visible; Risk II button disabled when no data */}
      <RiskToggleBar
        riskLevels={riskLevels}
        effectiveRiskLevel={effectiveRiskLevel}
        onSelect={setSelectedRiskLevel}
      />

      {/* Loading / error */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load price sheet. Please try again.
        </Alert>
      )}

      {data && !isLoading && data.products.length > 0 && (
        <>
          {/* Category filter tabs + search */}
          <Paper
            variant="outlined"
            sx={{ mb: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, gap: 1 }}>
              <Tabs
                value={activeCategory}
                onChange={(_, val) => setActiveCategory(val)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  flex: 1,
                  minHeight: 48,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    py: 0,
                  },
                }}
              >
                <Tab label="All" value="all" />
                {categories.map(cat => (
                  <Tab key={cat} label={cat} value={cat} />
                ))}
              </Tabs>
              <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
              <TextField
                size="small"
                placeholder="Search product..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ width: 220, my: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Paper>

          {/* Data grid */}
          <Paper
            variant="outlined"
            sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, overflow: 'hidden' }}
          >
            {grouped.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No products match the current filter.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 960 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      {[
                        { label: 'Code', align: 'left' as const },
                        { label: 'Name', align: 'left' as const },
                        { label: 'Description', align: 'left' as const },
                        { label: 'Size', align: 'left' as const },
                        { label: 'Retail Price/Unit', align: 'right' as const },
                        { label: 'Cost/Unit', align: 'right' as const },
                        { label: 'Cost/Ft', align: 'right' as const },
                        { label: 'Old MSRP', align: 'right' as const },
                        { label: 'New MSRP', align: 'right' as const },
                        { label: '% Change', align: 'right' as const },
                      ].map(col => (
                        <TableCell
                          key={col.label}
                          align={col.align}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            py: 1.25,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grouped.map(([category, items]) => {
                      const avgChange = categoryAverageChange(items);
                      return (
                        <React.Fragment key={category}>
                          {/* Category header */}
                          <TableRow>
                            <TableCell
                              colSpan={10}
                              sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                                py: 0.75,
                                px: 1.5,
                              }}
                            >
                              {category}
                              <Chip
                                label={isRiskII ? 'Risk II - Permanent Dwelling' : 'Risk I'}
                                size="small"
                                sx={{
                                  ml: 1,
                                  height: 18,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  bgcolor: isRiskII ? '#fff3e0' : '#e3f2fd',
                                  color: isRiskII ? '#e65100' : '#1565c0',
                                }}
                              />
                            </TableCell>
                          </TableRow>

                          {/* Product rows */}
                          {items.map((item, idx) => {
                            // Dealer Cost/Unit = MSRP × configured margin factor for the row's tier
                            const costPerUnit = productCostPerUnit(item);
                            const costPerFt = productCostPerFoot(item);
                            const hasOldMsrp = hasPreviousMsrp(item.priceHistory);
                            const showPctChange = shouldShowPercentChange(item.priceHistory);
                            return (
                              <TableRow
                                key={`${category}-${idx}`}
                                hover
                                sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                              >
                                <TableCell
                                  sx={{
                                    fontSize: '0.8125rem',
                                    fontFamily: 'monospace',
                                    color: 'text.secondary',
                                  }}
                                >
                                  {item.code ?? '—'}
                                  {item.isPriced === false && (
                                    <Tooltip title={(item.missingMaterials ?? []).join(', ')}>
                                      <Chip
                                        label="Missing Price"
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                                      />
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                                  {deriveProductLabel(item)}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                                  {fmtSize(item.averageSize)}'{' '}
                                  {(item.productType ?? 'truss').toLowerCase()} and assembly
                                  hardware
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.8125rem' }}>
                                  {fmtSize(item.averageSize)}'
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontSize: '0.8125rem',
                                    fontFamily: 'monospace',
                                    fontWeight: 600,
                                    color: 'primary.main',
                                  }}
                                >
                                  {fmtCurrency(item.pricing.msrp)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontSize: '0.8125rem', fontFamily: 'monospace' }}
                                >
                                  {fmtCurrency(costPerUnit)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontSize: '0.8125rem', fontFamily: 'monospace' }}
                                >
                                  {fmtCurrency(costPerFt)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontSize: '0.8125rem',
                                    fontFamily: 'monospace',
                                    color: 'text.secondary',
                                  }}
                                >
                                  {hasOldMsrp && item.priceHistory?.previousMsrp != null
                                    ? fmtCurrency(item.priceHistory.previousMsrp)
                                    : 'N/A'}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontSize: '0.8125rem', fontFamily: 'monospace' }}
                                >
                                  {fmtCurrency(item.pricing.msrp)}
                                </TableCell>
                                <TableCell align="right">
                                  {showPctChange &&
                                  item.priceHistory?.msrpChangePercentage != null ? (
                                    <PercentChange value={item.priceHistory.msrpChangePercentage} />
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            );
                          })}

                          {/* Category average footer row */}
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell
                              colSpan={9}
                              align="right"
                              sx={{
                                fontSize: '0.75rem',
                                fontStyle: 'italic',
                                color: 'text.secondary',
                                py: 0.5,
                              }}
                            >
                              Average:
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }}>
                              <PercentChange value={avgChange} />
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Footer */}
            <FooterRow
              filtered={filtered}
              isRiskII={isRiskII}
              riskFilteredTotal={riskFiltered.length}
            />
          </Paper>
        </>
      )}

      {data && !isLoading && data.products.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No published pricing data available for this manufacturer.
        </Alert>
      )}
    </Box>
  );
};

export default ProductPriceSheetPage;
